import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import {
    convertLayerInfoToPoints,
    // filterSmartGuidesLayers,
    getLayerInfoWithRotate,
    sampleLayersUniformly,
} from '@/utils/layer-kit';
import {
    DirectionType,
    type ILayerInfo,
    type ILayerProps,
    type IPoint,
    type IPointsInfo,
    type IPosition,
    type ISize,
    type ITransform,
} from '@/version-types';
import type { SmartGuideBlock } from '../../types';
import { BlockDirectionMap } from '../constants';
import type { BlockDirectionType, BlockInfo } from '../types';
import type BlockList from './block-list';

/** 参与等间距判断的最小投影重叠量，单位为编辑器逻辑坐标。 */
const MIN_OVERLAY = 3;
/** 可渲染等间距块的最小间距，单位为编辑器逻辑坐标。 */
const MIN_SPACING = 3;

type NearestLine = {
    /** 移动目标需要的偏移量，单位为编辑器逻辑坐标。 */
    distance: number;
    /** 移动后的目标坐标，单位为编辑器逻辑坐标。 */
    position: number;
};

/** 单个图层四周的等间距关系索引。 */
export class LayerBlock {
    /** 图层左侧按间距分组的关系。 */
    public leftBlocks: Map<number, BlockInfo[]>;
    /** 图层右侧按间距分组的关系。 */
    public rightBlocks: Map<number, BlockInfo[]>;
    /** 图层上方按间距分组的关系。 */
    public upBlocks: Map<number, BlockInfo[]>;
    /** 图层下方按间距分组的关系。 */
    public downBlocks: Map<number, BlockInfo[]>;

    /** 当前索引对应的图层 ID。 */
    public id: string;
    /** 当前图层旋转包围盒尺寸。 */
    public size: ISize;
    /** 当前图层旋转包围盒左上位置。 */
    public position: IPosition;

    constructor(id: string) {
        this.id = id;
        this.leftBlocks = new Map();
        this.rightBlocks = new Map();
        this.upBlocks = new Map();
        this.downBlocks = new Map();
        this.position = { left: 0, top: 0 };
        this.size = { width: 0, height: 0 };
    }

    collectBlocks(layer: ILayerProps, layers: ILayerProps[], activeLayer: ILayerProps, container: ISize) {
        const layerA = getLayerInfoWithRotate(layer);
        const aPoints = convertLayerInfoToPoints(layerA);

        const { width, height, left, top } = layerA;
        this.size = { width, height };
        this.position = { left, top };

        sampleLayersUniformly(layers, activeLayer).forEach((item) => {
            if (layer.id === item.id) return;
            const layerB = getLayerInfoWithRotate(item);
            const bPoints = convertLayerInfoToPoints(layerB);
            const { id } = item;
            this.pushLeftBlock(id, aPoints, bPoints);
            this.pushRightBlock(id, aPoints, bPoints);
            this.pushUpBlock(id, aPoints, bPoints);
            this.pushDownBlock(id, aPoints, bPoints);
        });

        // filterSmartGuidesLayers(layers, activeLayer, (item: ILayerProps) => {
        //     if (layer.id === item.id) return;
        //     const layerB = getLayerInfoWithRotate(item);
        //     const bPoints = convertLayerInfoToPoints(layerB);
        //     const { id } = item;
        //     this.pushLeftBlock(id, aPoints, bPoints);
        //     this.pushRightBlock(id, aPoints, bPoints);
        //     this.pushUpBlock(id, aPoints, bPoints);
        //     this.pushDownBlock(id, aPoints, bPoints);
        // });
        this.pushContainer('container', layerA, container);
        return this;
    }

    getNearest(layer: ITransform, blocksInfo: BlockList | undefined) {
        if (!blocksInfo) return;
        const layerRect = getLayerInfoWithRotate(layer);
        // 获取所有图层的 垂直/水平 距离去重
        // 校验用当前距离是否可以有效触发等间距
        // 计算图层移动到当前距离需要完成的吸附距离，取出最小值
        // 对比最小值，完成吸附
        const vertical: NearestLine[] = [];
        const horizontal: NearestLine[] = [];
        blocksInfo.forEach((item) => {
            item.leftBlocks.forEach((items) => {
                items.forEach((block) => {
                    const { size, position } = block.layerBlock;
                    // 图层在块内的两边间距
                    const spacing = (block.width - layer.width) / 2;
                    const expectLeft = block.left + spacing;
                    vertical.push({
                        distance: expectLeft - layer.left,
                        position: expectLeft,
                    });

                    const spacing2 = block.width;
                    const blockLayerRight = position.left + size.width;
                    const expectLeft2 = blockLayerRight + spacing2;
                    // 图层在 block 之外, 图层到 block 对应图层的距离 = block 的大小
                    vertical.push({
                        distance: expectLeft2 - layerRect.left,
                        position: expectLeft2,
                    });
                });
            });
            item.rightBlocks.forEach((items) => {
                items.forEach((block) => {
                    const { position } = block.layerBlock;
                    const spacing = (block.width - layer.width) / 2;
                    const expectLeft = block.left + spacing;
                    vertical.push({
                        distance: expectLeft - layerRect.left,
                        position: expectLeft,
                    });
                    // 图层在 block 之外, 图层到 block 对应图层的距离 = block 的大小
                    const spacing2 = block.width;
                    const expectLeft2 = position.left - spacing2 - layerRect.width;
                    vertical.push({
                        distance: expectLeft2 - layerRect.left,
                        position: expectLeft2,
                    });
                });
            });
            item.upBlocks.forEach((items) => {
                items.forEach((block) => {
                    const { size, position } = block.layerBlock;
                    // 图层在块内的两边间距
                    const spacing = (block.height - layer.height) / 2;
                    const expectTop = block.top + spacing;

                    horizontal.push({
                        distance: expectTop - layerRect.top,
                        position: expectTop,
                    });

                    const spacing2 = block.height;
                    const blockLayerBottom = position.top + size.height;
                    const expectTop2 = blockLayerBottom + spacing2;
                    // 图层在 block 之外, 图层到 block 对应图层的距离 = block 的大小
                    horizontal.push({
                        distance: expectTop2 - layerRect.top,
                        position: expectTop2,
                    });
                });
            });
            item.downBlocks.forEach((items) => {
                items.forEach((block) => {
                    const { position } = block.layerBlock;
                    const spacing = (block.height - layer.height) / 2;
                    const expectTop = block.top + spacing;
                    horizontal.push({
                        distance: expectTop - layerRect.top,
                        position: expectTop,
                    });
                    // 图层在 block 之外, 图层到 block 对应图层的距离 = block 的大小
                    const spacing2 = block.height;
                    const expectTop2 = position.top - spacing2 - layerRect.height;
                    horizontal.push({
                        distance: expectTop2 - layerRect.top,
                        position: expectTop2,
                    });
                });
            });
        });

        if (vertical.length) {
            return {
                vertical: sortBy(vertical, (p) => Math.abs(p.distance))[0],
                horizontal: sortBy(horizontal, (p) => Math.abs(p.distance))[0],
            };
        }

        return null;
    }

    getNearestForResize(layer: ITransform, blocksInfo: BlockList | undefined, directionX: number, directionY: number) {
        if (!blocksInfo) return;
        const layerRect = getLayerInfoWithRotate(layer);
        // 获取所有图层的 垂直/水平 距离去重
        // 校验用当前距离是否可以有效触发等间距
        // 计算图层移动到当前距离需要完成的吸附距离，取出最小值
        // 对比最小值，完成吸附
        const vertical: NearestLine[] = [];
        const horizontal: NearestLine[] = [];

        const isPositiveX = directionX === DirectionType.POSITIVE;
        const isOppositeX = directionX === DirectionType.OPPOSITE;

        const isPositiveY = directionY === DirectionType.POSITIVE;
        const isOppositeY = directionY === DirectionType.OPPOSITE;

        blocksInfo.forEach((blocks) => {
            blocks.leftBlocks.forEach((leftBlocks) => {
                leftBlocks.forEach((leftBlock) => {
                    const { size, position } = leftBlock.layerBlock;
                    const blockRight = leftBlock.left + leftBlock.width;
                    const layerRight = layerRect.left + layerRect.width;
                    // 产生这个 block 的图层的 right
                    const blockLayerRight = position.left + size.width;

                    // 拖动左边, 右边距固定, 拖动右边, 左边距固定
                    const spacing = isPositiveX ? layerRect.left - leftBlock.left : blockRight - layerRight;

                    // 图层应该有的宽度 = 区块宽度 - 左右两边的距离
                    // 位置如: spacing ↔ layer ↔ spacing
                    const expectWidth = leftBlock.width - spacing * 2;
                    // 还需要调整的宽度 = 期望的宽度 - 图层的当前宽度
                    const widthOffset = expectWidth - layerRect.width;

                    vertical.push({
                        distance: widthOffset,
                        position: isPositiveX ? layerRect.left : layerRect.left - widthOffset,
                    });

                    // 对比右边界
                    // if (isPositive) {
                    //     // 图层到容器右边的边距
                    //     const spacing2 = item.width;
                    //     // 图层应该有的宽度 = 区块宽度 - 左右两边的距离
                    //     const expectLeft = blockLayerRight + spacing2;
                    //     // 还需要调整的宽度 = 期望的宽度 - 图层的当前宽度
                    //     const widthOffset2 = expectLeft - layerRect.left;

                    //     vertical.push({
                    //         distance: widthOffset2,
                    //         position: expectLeft,
                    //     });
                    // }

                    /**
                     * 图层在 block 之外(右侧)
                     * 只有调整左边的时候, 会形成: (图层到 blockRight 的距离) = (block 的大小)
                     */
                    if (isOppositeX) {
                        // 边距就是 block 的宽度
                        const spacing = leftBlock.width;
                        // 目标图层的 left, 位置如: spacing(block) ↔ blockLayer ↔ spacing ↔ layer
                        const expectLeft = blockLayerRight + spacing;
                        // 宽度差(left 差)
                        const widthOffset = layerRect.left - expectLeft;
                        vertical.push({
                            distance: widthOffset,
                            position: expectLeft,
                        });
                    }
                });
            });
            blocks.rightBlocks.forEach((rightBlocks) => {
                rightBlocks.forEach((block) => {
                    const blockRight = block.left + block.width;
                    const layerRight = layerRect.left + layerRect.width;
                    // 拖动左边, 右边距固定, 拖动右边, 左边距固定
                    const spacing = isPositiveX ? layerRect.left - block.left : blockRight - layerRight;
                    // 图层应该有的宽度 = 区块宽度 - 左右两边的距离
                    // 位置如: spacing ↔ layer ↔ spacing
                    const expectWidth = block.width - spacing * 2;
                    // 还需要调整的宽度 = 期望的宽度 - 图层的当前宽度
                    const widthOffset = expectWidth - layerRect.width;

                    vertical.push({
                        distance: widthOffset,
                        position: isPositiveX ? layerRect.left : layerRect.left - widthOffset,
                    });

                    const { position } = block.layerBlock;

                    // 对比左边界
                    // if (isOpposite) {
                    //     // 间距等于 block 对应图层距离画布左侧的边距
                    //     const spacing2 = position.left;
                    //     // 图层的 left = 区块 left + 左右两边的距离
                    //     const expectLeft = item.left + spacing2;
                    //     // 还需要调整的宽度 = 期望的宽度 - 图层的当前宽度
                    //     const widthOffset2 = expectLeft - layerRect.left;

                    //     vertical.push({
                    //         // 移动目标需要的偏移量
                    //         distance: widthOffset2,
                    //         // 移动到目录后的 x 坐标
                    //         position: expectLeft,
                    //     });
                    // }

                    /**
                     * 图层在 block 之外(左侧)
                     * 只有调整右边的时候, 会形成: (图层 到 形成 block 的图层 的距离) = (block 的大小)
                     */
                    if (isPositiveX) {
                        // 边距就是 block 的宽度
                        const spacing2 = block.width;
                        // 目标图层的 left, 位置如: layer ↔ spacing ↔ blockLayer ↔ spacing(block)
                        const expectRight = position.left - spacing2;
                        // 宽度差(right 差)
                        const widthOffset3 = expectRight - layerRight;
                        vertical.push({
                            distance: widthOffset3,
                            position: expectRight,
                        });
                    }
                });
            });
            blocks.upBlocks.forEach((upBlocks) => {
                upBlocks.forEach((upBlock) => {
                    const { size, position } = upBlock.layerBlock;
                    const blockBottom = upBlock.top + upBlock.height;
                    const layerBottom = layerRect.top + layerRect.height;
                    // 产生这个 block 的图层的 bottom
                    const blockLayerBottom = position.top + size.height;

                    // 拖动左边, 右边距固定, 拖动右边, 左边距固定
                    const spacing = isPositiveY ? layerRect.top - upBlock.top : blockBottom - layerBottom;

                    // 图层应该有的宽度 = 区块宽度 - 左右两边的距离
                    // 位置如: spacing ↔ layer ↔ spacing
                    const expectHeight = upBlock.height - spacing * 2;
                    // 还需要调整的宽度 = 期望的宽度 - 图层的当前宽度
                    const heightOffset = expectHeight - layerRect.height;

                    horizontal.push({
                        distance: heightOffset,
                        position: isPositiveY ? layerRect.top : layerRect.top - heightOffset,
                    });

                    /**
                     * 图层在 block 之外(下方)
                     * 只有调整上边的时候, 会形成: (图层到 blockBottom 的距离) = (block 的大小)
                     */
                    if (isOppositeY) {
                        // 边距就是 block 的高度
                        const spacing = upBlock.height;
                        // 目标图层的 top, 位置如: spacing(block) ↔ blockLayer ↔ spacing ↔ layer
                        const expectTop = blockLayerBottom + spacing;
                        // 宽度差(top 差)
                        const heightOffset = layerRect.top - expectTop;
                        horizontal.push({
                            distance: heightOffset,
                            position: expectTop,
                        });
                    }
                });
            });
            blocks.downBlocks.forEach((downBlocks) => {
                downBlocks.forEach((block) => {
                    const blockBottom = block.top + block.height;
                    const layerBottom = layerRect.top + layerRect.height;
                    // 拖动左边, 右边距固定, 拖动右边, 左边距固定
                    const spacing = isPositiveY ? layerRect.top - block.top : blockBottom - layerBottom;

                    // 图层应该有的宽度 = 区块宽度 - 左右两边的距离
                    // 位置如: spacing ↔ layer ↔ spacing
                    const expectHeight = block.height - spacing * 2;

                    // 还需要调整的宽度 = 期望的宽度 - 图层的当前宽度
                    const heightOffset = expectHeight - layerRect.height;

                    horizontal.push({
                        distance: heightOffset,
                        position: isPositiveY ? layerRect.top : layerRect.top - heightOffset,
                    });

                    const { position } = block.layerBlock;

                    /**
                     * 图层在 block 之外(上方)
                     * 只有调整下边的时候, 会形成: (图层 到 形成 block 的图层 的距离) = (block 的大小)
                     */
                    if (isPositiveY) {
                        // 边距就是 block 的宽度
                        const spacing2 = block.height;
                        // 目标图层的 top, 位置如: layer ↔ spacing ↔ blockLayer ↔ spacing(block)
                        const expectBottom = position.top - spacing2;
                        // 宽度差(bottom 差)
                        const heightOffset = expectBottom - layerBottom;
                        horizontal.push({
                            distance: heightOffset,
                            position: expectBottom,
                        });
                    }
                });
            });
        });

        if (vertical.length) {
            return {
                vertical: sortBy(vertical, (p) => Math.abs(p.distance))[0],
                horizontal: sortBy(horizontal, (p) => Math.abs(p.distance))[0],
            };
        }

        return null;
    }

    getBlocks(blocksInfo: BlockList | undefined) {
        if (!blocksInfo) return [] as SmartGuideBlock[];
        const h = this.getRenderBlocks(
            this.getHorizontalSpacings(),
            BlockDirectionMap.left,
            BlockDirectionMap.right,
            blocksInfo,
        );
        const v = this.getRenderBlocks(
            this.getVerticalSpacings(),
            BlockDirectionMap.up,
            BlockDirectionMap.down,
            blocksInfo,
        );

        return h.concat(v).map(({ id, isHorizontal, left, top, width, height }) => ({
            id,
            isHorizontal,
            left,
            top,
            width,
            height,
        }));
    }

    getBlocksWithSpacing(spacing: number, direction: BlockDirectionType) {
        if (direction === BlockDirectionMap.left) {
            return this.leftBlocks.get(spacing) || [];
        } else if (direction === BlockDirectionMap.right) {
            return this.rightBlocks.get(spacing) || [];
        } else if (direction === BlockDirectionMap.up) {
            return this.upBlocks.get(spacing) || [];
        } else if (direction === BlockDirectionMap.down) {
            return this.downBlocks.get(spacing) || [];
        }
        return [];
    }

    private getRelationBlocks(spacing: number, dir: BlockDirectionType, blocksInfo: BlockList) {
        const resData: BlockInfo[] = [];
        const blocks = this.getBlocksWithSpacing(spacing, dir);
        resData.push(...blocks);
        blocks.forEach((item) => {
            const relationBlocks = blocksInfo.getBlocks(item.id, spacing, dir);
            if (relationBlocks) resData.push(...relationBlocks);
        });
        return resData;
    }

    private getRenderBlocks(data: number[], dir1: BlockDirectionType, dir2: BlockDirectionType, blocksInfo: BlockList) {
        for (let i = 0, len = data.length; i < len; i++) {
            const equalCoordData = [data[i], data[i] - 1, data[i] + 1];
            for (let j = 0; j < equalCoordData.length; j++) {
                const spacing1 = equalCoordData[j];
                for (let k = 0; k < equalCoordData.length; k++) {
                    const spacing2 = equalCoordData[k];

                    const dir1Blocks = this.getRelationBlocks(spacing1, dir1, blocksInfo);
                    const dir2Blocks = this.getRelationBlocks(spacing2, dir2, blocksInfo);
                    const resBlocks = uniqBy(
                        dir1Blocks.concat(dir2Blocks),
                        (p) => `${p.left}-${p.width}-${p.top}-${p.height}`,
                    );
                    let checkRes: BlockInfo[];
                    if (dir1 === BlockDirectionMap.left) {
                        checkRes = uniqBy(resBlocks, (p) => `${p.left}-${p.width}`);
                    } else {
                        checkRes = uniqBy(resBlocks, (p) => `${p.top}-${p.height}`);
                    }
                    if (checkRes.length > 2 || (checkRes.length == 2 && checkRes[0].id !== checkRes[1].id)) {
                        return resBlocks;
                    }
                }
            }
        }
        return [] as BlockInfo[];
    }

    private getVerticalSpacings() {
        const upSpacings = Array.from(this.upBlocks.keys());
        const downSpacings = Array.from(this.downBlocks.keys());
        return sortBy(upSpacings.concat(downSpacings));
    }

    private getHorizontalSpacings() {
        const leftSpacings = Array.from(this.leftBlocks.keys());
        const rightSpacings = Array.from(this.rightBlocks.keys());
        return sortBy(leftSpacings.concat(rightSpacings));
    }

    private pushDownBlock(id: string, aPoints: IPointsInfo, bPoints: IPointsInfo) {
        const spacing = bPoints.lt.y - aPoints.lb.y;
        if (spacing < MIN_SPACING) return;
        if (bPoints.rt.x < aPoints.lb.x || bPoints.lt.x > aPoints.rb.x) return;
        const points: IPoint[] = sortBy([aPoints.lb, aPoints.rb, bPoints.lt, bPoints.rt], (p) => p.x);
        const overlay = points[2].x - points[1].x;
        if (overlay < MIN_OVERLAY) return;

        this.pushDown({
            id,
            left: points[1].x,
            top: aPoints.lb.y,
            width: overlay,
            height: spacing,
            isHorizontal: false,
            direction: BlockDirectionMap.down,
            layerBlock: this,
        });
    }

    private pushUpBlock(id: string, aPoints: IPointsInfo, bPoints: IPointsInfo) {
        const spacing = aPoints.lt.y - bPoints.lb.y;
        if (spacing < MIN_SPACING) return;
        if (bPoints.rb.x < aPoints.lt.x || bPoints.lb.x > aPoints.rt.x) return;
        const points: IPoint[] = sortBy([aPoints.lt, aPoints.rt, bPoints.lb, bPoints.rb], (p) => p.x);
        const overlay = points[2].x - points[1].x;
        if (overlay < MIN_OVERLAY) return;

        this.pushUp({
            id,
            left: points[1].x,
            top: bPoints.lb.y,
            width: overlay,
            height: spacing,
            isHorizontal: false,
            direction: BlockDirectionMap.up,
            layerBlock: this,
        });
    }

    private pushLeftBlock(id: string, aPoints: IPointsInfo, bPoints: IPointsInfo) {
        // 当前图层的左边 - 对照图层的右边, 得出两个图层的距离
        const spacing = aPoints.lt.x - bPoints.rt.x;
        if (spacing < MIN_SPACING) return;
        // 判断两个图层在纵向有没有交集(有部分区域处于同一水平线上)
        if (bPoints.rb.y < aPoints.lt.y || bPoints.rt.y > aPoints.lb.y) return;
        const points: IPoint[] = sortBy([aPoints.lt, aPoints.lb, bPoints.rt, bPoints.rb], (p) => p.y);
        const overlay = points[2].y - points[1].y;
        if (overlay < MIN_OVERLAY) return;

        this.pushLeft({
            id,
            left: bPoints.rt.x,
            top: points[1].y,
            width: spacing,
            height: overlay,
            isHorizontal: true,
            direction: BlockDirectionMap.left,
            layerBlock: this,
        });
    }

    private pushRightBlock(id: string, aPoints: IPointsInfo, bPoints: IPointsInfo) {
        const spacing = bPoints.lt.x - aPoints.rt.x;
        if (spacing < MIN_SPACING) return;
        if (bPoints.lb.y < aPoints.rt.y || bPoints.lt.y > aPoints.rb.y) return;
        const points: IPoint[] = sortBy([aPoints.rt, aPoints.rb, bPoints.lt, bPoints.lb], (p) => p.y);
        const overlay = points[2].y - points[1].y;
        if (overlay < MIN_OVERLAY) return;
        this.pushRight({
            id,
            left: aPoints.rt.x,
            top: points[1].y,
            width: spacing,
            height: overlay,
            isHorizontal: true,
            direction: BlockDirectionMap.right,
            layerBlock: this,
        });
    }

    private pushContainer(id, layer: ILayerInfo, container: ISize) {
        this.pushLeft({
            id,
            left: 0,
            top: layer.top,
            width: layer.left,
            height: layer.height,
            isHorizontal: true,
            direction: BlockDirectionMap.left,
            layerBlock: this,
        });
        this.pushRight({
            id,
            left: layer.left + layer.width,
            top: layer.top,
            width: container.width - (layer.left + layer.width),
            height: layer.height,
            isHorizontal: true,
            direction: BlockDirectionMap.right,
            layerBlock: this,
        });
        this.pushUp({
            id,
            left: layer.left,
            top: 0,
            width: layer.width,
            height: layer.top,
            isHorizontal: false,
            direction: BlockDirectionMap.up,
            layerBlock: this,
        });
        this.pushDown({
            id,
            left: layer.left,
            top: layer.top + layer.height,
            width: layer.width,
            height: container.height - (layer.top + layer.height),
            isHorizontal: false,
            direction: BlockDirectionMap.down,
            layerBlock: this,
        });
    }

    private pushUp(info: BlockInfo) {
        const spacing = info.height;
        [spacing].forEach((item) => {
            if (this.upBlocks.has(item)) {
                this.upBlocks.get(item)?.push(info);
            } else {
                this.upBlocks.set(item, [info]);
            }
        });
    }

    private pushDown(info: BlockInfo) {
        const spacing = info.height;
        [spacing].forEach((item) => {
            if (this.downBlocks.has(item)) {
                this.downBlocks.get(item)?.push(info);
            } else {
                this.downBlocks.set(item, [info]);
            }
        });
    }

    private pushLeft(info: BlockInfo) {
        const spacing = info.width;
        [spacing].forEach((item) => {
            if (this.leftBlocks.has(item)) {
                this.leftBlocks.get(item)?.push(info);
            } else {
                this.leftBlocks.set(item, [info]);
            }
        });
    }

    private pushRight(info: BlockInfo) {
        const spacing = info.width;
        [spacing].forEach((item) => {
            if (this.rightBlocks.has(item)) {
                this.rightBlocks.get(item)?.push(info);
            } else {
                this.rightBlocks.set(item, [info]);
            }
        });
    }
}

export default LayerBlock;
